import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

interface SupportNotificationsContextValue {
  totalUnread: number;
  unreadByTicket: Record<number, number>;
  refresh: () => void;
  markTicketRead: (ticketId: number) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const SupportNotificationsContext = createContext<SupportNotificationsContextValue>({
  totalUnread: 0,
  unreadByTicket: {},
  refresh: () => {},
  markTicketRead: async () => {},
  markAllRead: async () => {},
});

export function SupportNotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [totalUnread, setTotalUnread] = useState(0);
  const [unreadByTicket, setUnreadByTicket] = useState<Record<number, number>>({});

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setTotalUnread(0);
      setUnreadByTicket({});
      return;
    }
    try {
      const [countRes, byTicketRes] = await Promise.all([
        fetch(`${BASE_URL}/api/support/notifications/unread-count`, { credentials: "include" }),
        fetch(`${BASE_URL}/api/support/notifications/unread-by-ticket`, { credentials: "include" }),
      ]);
      if (countRes.ok) {
        const data = await countRes.json();
        setTotalUnread(data.count ?? 0);
      }
      if (byTicketRes.ok) {
        const data = await byTicketRes.json();
        setUnreadByTicket(data ?? {});
      }
    } catch {
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markTicketRead = useCallback(async (ticketId: number) => {
    try {
      await fetch(`${BASE_URL}/api/support/notifications/read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ticketId }),
      });
      setUnreadByTicket((prev) => {
        const next = { ...prev };
        delete next[ticketId];
        return next;
      });
      setTotalUnread((prev) => Math.max(0, prev - (unreadByTicket[ticketId] ?? 0)));
    } catch {
    }
  }, [unreadByTicket]);

  const markAllRead = useCallback(async () => {
    try {
      await fetch(`${BASE_URL}/api/support/notifications/read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      setUnreadByTicket({});
      setTotalUnread(0);
    } catch {
    }
  }, []);

  return (
    <SupportNotificationsContext.Provider value={{ totalUnread, unreadByTicket, refresh: fetchNotifications, markTicketRead, markAllRead }}>
      {children}
    </SupportNotificationsContext.Provider>
  );
}

export function useSupportNotifications() {
  return useContext(SupportNotificationsContext);
}
